/* eslint-disable @typescript-eslint/no-empty-function */
import { FC, useEffect, useState, useMemo } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import styles from '../DeactivateProductsDrawer/DeactivateProductsDrawer.module.css';
import { ActivateModifiersDrawerProps } from './ActivateModifiersDrawer.types';
import { t } from 'i18next';
import { useUpdateProductPrice } from '@/services/modifiers/modifiers.service';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import { EditDrawerProcessing } from '../../SharedComponents/EditDrawerProcessing';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { PriceUpdateAPIResponse } from '@/types/stores.types';
import { useSTKContext } from '@/context/context';
import { getStores } from '@/services/menu/menus.helper.service';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import { VariantModifierPriceUpdateRequest } from '@/types/menus.types';
import ModifiersTable from '../DeactivateModifiersDrawer/DeactivateModifiersDrawerTabItems/ModifiersTable';

export const ActivateModifiersDrawer: FC<ActivateModifiersDrawerProps> = ({
  isOpen,
  onClose,
  storeModifiers,
  modifierCategory,
  afterSave,
  setPriceDeviationsData: _setPriceDeviationsData,
}) => {
  void _setPriceDeviationsData;
  const [state, setState] = useState(isOpen);
  const [isActivating, setIsActivating] = useState(false);
  const [activationComplete, setActivationComplete] = useState(false);
  const [activationResponse, setActivationResponse] = useState<PriceUpdateAPIResponse | undefined>();
  const { storesAndGroups } = useSTKContext();

  const { mutate: activateModifiers } = useUpdateProductPrice();

  useEffect(() => {
    setState(isOpen);
    if (!isOpen) {
      setIsActivating(false);
      setActivationComplete(false);
      setActivationResponse(undefined);
    }
  }, [isOpen]);

  const requestPayload = useMemo((): VariantModifierPriceUpdateRequest[] => {
    const selectedStores = getStores(storesAndGroups);
    return storeModifiers.map((m) => ({
      stores: selectedStores,
      modifierKeys: {
        variantId: m.variantId ?? '',
        slotId: m.slotId,
        modifierId: m.modifierId,
        modifierWeightId: m.modifierWeightId,
      },
      price: '0',
      modifierCategory,
    }));
  }, [storeModifiers, storesAndGroups, modifierCategory]);

  const uniqueStoreCount = useMemo(() => {
    const uniqueStores = new Set<string>();
    requestPayload.forEach((request) => {
      request.stores?.forEach((store) => uniqueStores.add(store));
    });
    return uniqueStores.size;
  }, [requestPayload]);

  const handleConfirmActivation = () => {
    if (requestPayload.length === 0) {
      return;
    }
    setIsActivating(true);
    activateModifiers(requestPayload, {
      onSuccess: (response) => {
        setActivationResponse(response as PriceUpdateAPIResponse);
        setActivationComplete(true);
        setIsActivating(false);
        afterSave();
      },
      onError: (error: any) => {
        if (
          error?.response?.status === 504 ||
          error?.code === 'ECONNABORTED' ||
          error?.message?.includes('timeout') ||
          error?.message?.includes('Gateway Timeout')
        ) {
          const timeoutResponse = {
            failedStores: [],
            status: 'TIMEOUT',
            message: t('requestProcessingModal.timeout.message'),
          } as unknown as PriceUpdateAPIResponse;
          setActivationResponse(timeoutResponse);
          setActivationComplete(true);
          setIsActivating(false);
          afterSave();
          return;
        }
        const errorResponse = {
          failedStores: [],
          status: 'ERROR',
          message: error?.message || t('activateModifiers.errorMessage'),
        } as unknown as PriceUpdateAPIResponse;
        setActivationResponse(errorResponse);
        setActivationComplete(true);
        setIsActivating(false);
        afterSave();
      },
    });
  };

  const onCloseDrawer = () => {
    setActivationComplete(false);
    setIsActivating(false);
    setActivationResponse(undefined);
    setState(false);
    onClose();
  };

  return (
    <div>
      <SwipeableDrawer open={state} onClose={onCloseDrawer} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>{t('activateModifiers.drawerTitle')}</h1>
            </div>
            <button type="button" onClick={onCloseDrawer}>
              <X data-testid="x-icon" size={24} color="var(--black-900)" />
            </button>
          </header>
          {(isActivating || (activationComplete && activationResponse)) && (
            <div className={styles.alertBanner}>
              <CalendarAlert />
            </div>
          )}

          <div className={styles.content}>
            {isActivating ? (
              <EditDrawerProcessing count={uniqueStoreCount} entityToUpdate={EntityToUpdate.MENU} />
            ) : activationComplete && activationResponse ? (
              <div className={styles.successFailureWrapper}>
                <EditDrawerSuccessAndFailure
                  apiResponse={activationResponse as PriceUpdateAPIResponse}
                  entityToUpdate={EntityToUpdate.MENU}
                />
              </div>
            ) : (
              <>
                <div className={styles.confirmationContent}>
                  <div className={styles.confirmationTitleSection}>
                    <h2 className={styles.confirmationTitle}>{t('editMenuPrice.submitMenuChanges')}</h2>
                    <div className={styles.confirmationDivider} data-testid="confirmation-divider" />
                    <p className={`${styles.deactivationInfo} ${styles.deactivationInfoSingleLine}`}>
                      {t('activateModifiers.ingredientsActivationInfoText')}
                    </p>
                  </div>

                  <div className={styles.tableWrapper}>
                    <ModifiersTable storeModifiers={storeModifiers} futureState={{ mode: 'activeCircle' }} />
                  </div>
                </div>

                <div className={styles.footer}>
                  <div className={styles.alertContainer}>
                    <CalendarAlert />
                  </div>

                  <div className={styles.buttonContainer}>
                    <Button
                      className={styles.cancelButton}
                      onClick={onCloseDrawer}
                      variant={ButtonVariants.Secondary}
                      type="button"
                      disabled={isActivating}
                    >
                      {t('editMenuPrice.cancel')}
                    </Button>
                    <Button
                      className={styles.submitButton}
                      onClick={handleConfirmActivation}
                      variant={ButtonVariants.Primary}
                      type="button"
                      isLoading={isActivating}
                      disabled={isActivating || requestPayload.length === 0}
                    >
                      {t('editMenuPrice.submitChanges')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </SwipeableDrawer>
    </div>
  );
};
